import { useLingui } from '@lingui/react/macro';

import { Button, Text } from '@rover/kibble/core';
import useTheme from '@rover/kibble/themes/useTheme';

/** Line height in pixels for overflow calculations */
export const LINE_HEIGHT = 24;

/** Number of lines for small size */
export const NUMBER_OF_LINES_SMALL = 2;
/** Number of lines for medium size */
export const NUMBER_OF_LINES_MEDIUM = 4;
/** Number of lines for large size */
export const NUMBER_OF_LINES_LARGE = 8;

/** Size presets determining number of visible lines */
export type OverflowTextSizes = 'small' | 'medium' | 'large';
/** Layout variants for expand/collapse button */
export type VariantSizes = 'inline' | 'block';

/** Props for OverflowText component */
export type Props = {
  /**
   * How many lines show while the text is collapsed, overriding `size`. Leave it out to use
   * `size`.
   */
  lines?: number;
  /**
   * How many lines show while the text is collapsed: `small` 2, `medium` 4 or `large` 8. Defaults
   * to `large`.
   */
  size?: OverflowTextSizes;
  /**
   * Where the button sits: `block` under the text, or `inline` on a white fade over the end of the
   * last visible line. Defaults to `block`.
   */
  variant?: VariantSizes;
  /** The label that expands the text, already translated. Defaults to "Read more". */
  readMoreText?: string;
  /** The label that collapses the text again, already translated. Defaults to "Read less". */
  readLessText?: string;
  /** The text to show. Line breaks in a string show as line breaks. */
  children?: React.ReactNode;
};

/** Props for expand/collapse button */
export type ExpandCollapseProps = {
  /** Current expanded state */
  isExpanded: boolean;
  /** Toggle expand/collapse */
  expandCollapseToggle: () => void;
  /** ID for paragraph element for ARIA */
  paragraphId: string;
  /** "Read more" button text */
  readMoreText: string;
  /** "Read less" button text */
  readLessText: string;
  /** Layout variant */
  variant: VariantSizes;
};

export const ExpandCollapseButton = ({
  isExpanded,
  expandCollapseToggle,
  paragraphId,
  readMoreText,
  readLessText,
  variant,
}: ExpandCollapseProps): JSX.Element => {
  const { t } = useLingui();
  const isNative = process.env.JS_ENV_NATIVE;
  const theme = useTheme();
  const { colors } = theme;

  let variantStyles = {};
  // the padding varies by variant and platform
  if (variant === 'inline' && isNative) {
    variantStyles = { paddingY: '0', paddingRight: '0', paddingLeft: '10x' };
  }
  if (variant === 'inline' && !isNative) {
    variantStyles = { paddingY: '0', paddingRight: '0', paddingLeft: '2x' };
  }
  if (variant === 'block') {
    variantStyles = { padding: '0' };
  }

  return (
    <Button
      data-testid="overflow-text-button"
      variant="flat"
      onClick={expandCollapseToggle}
      aria-expanded={isExpanded}
      aria-controls={paragraphId}
      aria-label={t`Reveal visually hidden text`}
      {...variantStyles}
    >
      <Text
        rxnProps={{
          style: {
            textDecorationLine: 'underline',
            color: colors.interactive_link.primary_base.toString(),
          },
        }}
      >
        {isExpanded ? readLessText : readMoreText}
      </Text>
    </Button>
  );
};

export const getTargetNumLines = (size: OverflowTextSizes, lines = 0): number => {
  if (lines > 0) {
    return lines;
  }
  switch (size) {
    case 'small':
      return NUMBER_OF_LINES_SMALL;
    case 'medium':
      return NUMBER_OF_LINES_MEDIUM;
    case 'large':
      return NUMBER_OF_LINES_LARGE;
    default:
      return NUMBER_OF_LINES_LARGE;
  }
};
